import type { AnalysisPreset } from "./analysis-presets";

/** Native Stockfish exposed by the Chessla desktop app's preload; absent in browsers. */
export interface DesktopStockfish {
  /** Release build id, e.g. `stockfish-18-native-x86-64-avx2`. */
  build: string;
  cores: number;
  /** Hash ceiling the desktop derived from installed memory. */
  maxHashMb: number;
  spawn(
    onLine: (line: string) => void,
    onExit: (reason: string) => void,
  ): { send(command: string): void; kill(): void };
}

/** The subset of `Worker` that StockfishWorker drives; native processes adapt to it. */
export interface EngineTransport {
  onmessage: ((event: MessageEvent<unknown>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  onmessageerror: ((event: MessageEvent) => void) | null;
  postMessage(command: string): void;
  terminate(): void;
}

declare global {
  var chesslaDesktop: { stockfish?: DesktopStockfish } | undefined;
}

export const desktopStockfish = (): DesktopStockfish | undefined => globalThis.chesslaDesktop?.stockfish;

/** Native search is not bound by WASM memory or browser thread limits; verifier searches stay reproducible. */
export const nativeSearchOptions = (preset: AnalysisPreset, stockfish: DesktopStockfish) =>
  preset.forceSingleThread
    ? { threads: 1, hash: preset.hash }
    : { threads: Math.max(1, stockfish.cores - 1), hash: Math.min(preset.hash * 4, stockfish.maxHashMb) };

export function createNativeTransport(stockfish: DesktopStockfish): EngineTransport {
  const transport: EngineTransport = {
    onmessage: null,
    onerror: null,
    onmessageerror: null,
    postMessage: (command) => engine.send(command),
    terminate: () => engine.kill(),
  };
  const engine = stockfish.spawn(
    (line) => transport.onmessage?.({ data: line } as MessageEvent<unknown>),
    (reason) =>
      transport.onerror?.({ message: `Native engine ${reason}`, preventDefault: () => undefined } as ErrorEvent),
  );
  return transport;
}
